import { useEffect, useState } from "react";

import { axios } from "../api";
import { allAircraftStatusesRequest } from "../aircraft/status";

export interface AircraftOption {
  id: number;
  registration: string;
}

/**
 * The tenant's real fleet, for aircraft pickers.
 *
 * Exists because four modules (tech-log, defect-reporting, trend,
 * modifications) each hardcoded the SAME five invented New Zealand
 * registrations --- ZK-HQL / ZK-IFR / ZK-FDX / ZK-NZA / ZK-TTA --- as their
 * aircraft dropdown. None was product-gated, so every tenant on every product
 * got a filter listing five aircraft it does not own. On g2 (a CANADIAN
 * operator, fleet C-Gxxx) the dropdown could not match a single real row: every
 * selection filtered the grid to nothing, which reads as "no data" rather than
 * "wrong dropdown". Aaron, 2026-08-23: "this doesn't match prod".
 *
 * Envelope is `items`, NOT `data`: app/aircraft/api.py:76 declares
 * response_model_by_alias=False, which suppresses pagination.py's items->data
 * alias on the wire. openapi renders the alias in BOTH cases and so cannot
 * answer this --- the decorator is the only oracle (memory
 * reference_openapi_not_envelope_or_field_oracle). Reading `.data.data` here
 * would leave every picker permanently empty with no error to explain it.
 *
 * One `size=100` fetch: `size` is capped at 100 server-side and over-asking
 * 422s rather than clamping (src/blank/PATTERNS.md, "Respect backend
 * pagination cap"). g2's fleet is 33 aircraft. A tenant above 100 aircraft
 * silently gets only the first page, so the correct end-state is a
 * search-as-you-type picker --- src/components/AircraftAutocomplete.tsx
 * already does that where a full FK is needed; this hook is for the simple
 * bounded dropdown case.
 *
 * Failure is SILENT and returns []. An empty picker is the honest answer when
 * the fleet could not be read; falling back to invented registrations is what
 * this hook exists to delete. Callers must render the empty case rather than
 * assuming at least one option.
 */
export function useAircraftOptions(): AircraftOption[] {
  const [options, setOptions] = useState<AircraftOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Every status: these options also resolve the aircraft of EXISTING
        // records (defect, trend, modification, maintenance log, calendar
        // lanes), and the list now defaults to Active (A422).
        const resp = await axios.get("/v6/aircraft", {
          ...allAircraftStatusesRequest({ page: 1, size: 100 }),
          suppressErrorToast: true,
        });
        const rows = resp?.data?.items ?? [];
        if (cancelled || !Array.isArray(rows)) return;
        setOptions(
          rows
            .filter((a: any) => a?.id != null && a?.registration)
            .map((a: any) => ({ id: a.id, registration: String(a.registration) }))
            .sort((a: AircraftOption, b: AircraftOption) =>
              a.registration.localeCompare(b.registration, "en", { sensitivity: "base" })
            )
        );
      } catch {
        /* honest empty: never fall back to invented registrations */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return options;
}
